/**
 * Vue des **effets de la run** (U-03) — logique pure, sans React et sans calcul de jeu.
 *
 * Principe (CDC §8, DECISIONS §6.8, §8) : **aucune valeur n'est recalculée ici**. Ce module se
 * contente de *choisir* et de *mettre en forme* ce que le serveur a déjà validé :
 *
 * - `run.multiplicateurs` : cumuls publiés par le serveur (`GET /runs/:id`), recopiés tels quels ;
 * - `run.jokers` : solde de jokers d'autorité, recopié tel quel ;
 * - `cartes` : définitions renvoyées par `POST /runs/:id/cards/choose` (`card`, `bonusCard`), telles
 *   que le serveur les a accordées. Aucun effet n'est déduit d'un catalogue local, et une carte
 *   dont le serveur n'a pas publié l'effet est signalée comme telle plutôt que devinée.
 *
 * Le seul travail local est une **déduplication par identifiant** (une carte obtenue deux fois ne
 * doit pas apparaître deux fois) : elle ne change aucune valeur, seulement la liste affichée.
 */
import type { CartePublique, MultiplicateursRun, Rarete, RunState } from '@elemora/shared/api';

/** Élément d'une carte : un des quatre, ou `neutre` (comme `GET /me/collection`). */
export type ElementCarte = 'feu' | 'eau' | 'terre' | 'air' | 'neutre';

/** Clés des statistiques publiées dans `multiplicateurs.parStat` (ordre d'affichage). */
export const CLES_MULTIPLICATEURS = [
  'degats',
  'portee',
  'cadenceMs',
  'vitesseProjectile',
  'coutTour',
  'coutAmelioration',
] as const;

export type CleMultiplicateur = (typeof CLES_MULTIPLICATEURS)[number];

/** Une carte obtenue pendant la run, telle qu'elle doit être affichée. */
export interface CarteRunVue {
  readonly cardId: string;
  /** Clé i18n du nom (`content.carte.<id>.name`), jamais un libellé figé. */
  readonly nameKey: string;
  readonly element: ElementCarte;
  readonly rarete: Rarete;
  /** Effets **publiés par le serveur** ; `null` s'il ne les a pas publiés (F13). */
  readonly effets: readonly unknown[] | null;
  /** Vrai si la carte porte au moins un effet de type `synergie`. */
  readonly synergie: boolean;
}

/** Cumuls tels que le serveur les publie, sans transformation. */
export interface MultiplicateursVue {
  /** Les six statistiques de `parStat`, dans l'ordre d'affichage. */
  readonly parStat: Readonly<Record<CleMultiplicateur, number>>;
  readonly gainEclats: number;
  /** Uniquement les éléments qui ont au moins un bonus non nul (triés par identifiant). */
  readonly parElement: readonly ElementMultiplicateursVue[];
  /** Nombre d'éléments publiés sans aucun bonus (pour le message « les autres… »). */
  readonly elementsSansBonus: number;
}

export interface ElementMultiplicateursVue {
  readonly element: string;
  readonly degats: number;
  readonly portee: number;
  readonly cadenceMs: number;
  readonly vitesseProjectile: number;
}

/** Vue complète du panneau : uniquement des valeurs serveur recopiées. */
export interface VueEffetsRun {
  readonly cartes: readonly CarteRunVue[];
  readonly multiplicateurs: MultiplicateursVue;
  readonly jokers: number;
  /** Cartes de synergie **obtenues** (donc découvertes, DECISIONS §7.5). */
  readonly synergies: readonly CarteRunVue[];
  /** Nombre de cartes publiées sans effet : le panneau le dit au lieu de l'inventer. */
  readonly cartesSansEffetPublie: number;
}

/** Vrai si l'effet publié est une synergie (liste fermée du DSL, DECISIONS §6.5). */
export function estEffetSynergie(effet: unknown): boolean {
  return (
    typeof effet === 'object' &&
    effet !== null &&
    (effet as Record<string, unknown>)['type'] === 'synergie'
  );
}

/** Élément publié d'une carte : `neutre` est admis, tout le reste est ramené à `neutre`. */
function elementDe(carte: CartePublique): ElementCarte {
  const element = carte.element;
  if (element === 'feu' || element === 'eau' || element === 'terre' || element === 'air') {
    return element;
  }
  return 'neutre';
}

/**
 * Convertit une carte **publiée par le serveur** en vue d'affichage.
 *
 * Aucune valeur n'est complétée ni corrigée : `effets` vaut `null` si le serveur ne les a pas
 * publiés, et le panneau l'annonce (« effet non publié ») au lieu de lire un catalogue local.
 */
export function versCarteRun(carte: CartePublique): CarteRunVue {
  const effets = carte.effects ?? null;
  return {
    cardId: carte.id,
    nameKey: carte.nameKey,
    element: elementDe(carte),
    rarete: carte.rarity,
    effets,
    synergie: effets !== null && effets.some(estEffetSynergie),
  };
}

/**
 * Ajoute les cartes d'une réponse serveur à la liste de la run, **sans doublon**.
 *
 * Utilisé pour les deux cartes qu'une clôture de vague peut accorder : la carte choisie et la
 * carte bonus de boss. L'ordre d'obtention est conservé (ordre des réponses serveur), et une carte
 * déjà présente n'est jamais dupliquée : le serveur ne compte lui-même qu'une fois par carte dans
 * `multiplicateurs` (déduplication côté serveur, `etat-public.ts`).
 */
export function ajouterCartesObtenues(
  existantes: readonly CartePublique[],
  nouvelles: readonly (CartePublique | null | undefined)[],
): readonly CartePublique[] {
  const resultat = [...existantes];
  const connues = new Set(resultat.map((carte) => carte.id));
  for (const carte of nouvelles) {
    if (carte === null || carte === undefined) continue;
    if (connues.has(carte.id)) continue;
    connues.add(carte.id);
    resultat.push(carte);
  }
  return resultat;
}

/**
 * Recopie `run.multiplicateurs` en vue d'affichage.
 *
 * **Aucune somme n'est refaite** : les valeurs affichées sont exactement celles du serveur, y
 * compris pour `parStat` qui additionne déjà les bonus sans filtre (DECISIONS §6.8). Un test
 * dédié vérifie qu'une valeur volontairement différente de la somme des cartes affichées est bien
 * celle du serveur qui s'affiche — c'est la garantie anti-divergence de la carte U-03.
 */
export function multiplicateursVue(multiplicateurs: MultiplicateursRun): MultiplicateursVue {
  const parElement: ElementMultiplicateursVue[] = [];
  let elementsSansBonus = 0;

  for (const element of Object.keys(multiplicateurs.parElement).sort()) {
    const valeurs = multiplicateurs.parElement[element];
    if (valeurs === undefined) continue;
    const actif =
      valeurs.degats !== 0 ||
      valeurs.portee !== 0 ||
      valeurs.cadenceMs !== 0 ||
      valeurs.vitesseProjectile !== 0;
    if (!actif) {
      elementsSansBonus += 1;
      continue;
    }
    parElement.push({
      element,
      degats: valeurs.degats,
      portee: valeurs.portee,
      cadenceMs: valeurs.cadenceMs,
      vitesseProjectile: valeurs.vitesseProjectile,
    });
  }

  return {
    parStat: {
      degats: multiplicateurs.parStat.degats,
      portee: multiplicateurs.parStat.portee,
      cadenceMs: multiplicateurs.parStat.cadenceMs,
      vitesseProjectile: multiplicateurs.parStat.vitesseProjectile,
      coutTour: multiplicateurs.parStat.coutTour,
      coutAmelioration: multiplicateurs.parStat.coutAmelioration,
    },
    gainEclats: multiplicateurs.gainEclats,
    parElement,
    elementsSansBonus,
  };
}

/**
 * Assemble la vue du panneau.
 *
 * `run` est l'état d'autorité (seule source des jokers et des multiplicateurs) et `cartes` la liste
 * des cartes accordées par le serveur pendant cette run. Le nombre de synergies est une simple
 * lecture de cette liste : une synergie n'est affichée que si le serveur l'a effectivement
 * accordée (F17 — les autres restent masquées, et ne sont même pas nommées).
 */
export function construireVueEffets(
  run: Pick<RunState, 'jokers' | 'multiplicateurs'>,
  cartes: readonly CartePublique[],
): VueEffetsRun {
  const vues = cartes.map(versCarteRun);
  return {
    cartes: vues,
    multiplicateurs: multiplicateursVue(run.multiplicateurs),
    jokers: run.jokers,
    synergies: vues.filter((carte) => carte.synergie),
    cartesSansEffetPublie: vues.filter((carte) => carte.effets === null).length,
  };
}
